// src/utils/layoutGraph.js
import dagre from '@dagrejs/dagre';

/**
 * Node dimension configuration based on node type
 */
const NODE_DIMENSIONS = {
    hierarchyNode: { width: 240, height: 70 },
    hierarchyNodeWithLeaders: { width: 240, height: 140 }, // Larger when leaders are shown
    userNode: { width: 200, height: 65 },
    unitNode: { width: 240, height: 70 },
};

/**
 * Default layout options for Dagre
 */
const DEFAULT_LAYOUT_OPTIONS = {
    rankdir: 'TB',      // Direction: TB (top-bottom), LR (left-right), BT, RL
    nodesep: 40,        // Horizontal separation between nodes
    ranksep: 80,        // Vertical separation between ranks/levels
    edgesep: 20,        // Separation between edges
    marginx: 20,        // Graph margin x
    marginy: 20,        // Graph margin y
    align: 'UL',        // Alignment: UL, UR, DL, DR
};

/**
 * Get the dimensions for a node based on its type and content
 * @param {Object} node - ReactFlow node object
 * @returns {{ width: number, height: number }}
 */
const getNodeDimensions = (node) => {
    if (node.type === 'hierarchyNode') {
        // Calculate height based on number of unit leaders
        const unitLeaders = node.data?.unitLeaders || [];
        if (unitLeaders.length > 0) {
            // Base height + additional height per leader
            const baseHeight = 70;
            const leaderHeight = 55; // Height per leader row
            const paddingHeight = 20; // Padding for leaders section
            return {
                width: NODE_DIMENSIONS.hierarchyNodeWithLeaders.width,
                height: baseHeight + paddingHeight + (unitLeaders.length * leaderHeight),
            };
        }
        return NODE_DIMENSIONS.hierarchyNode;
    }
    return NODE_DIMENSIONS[node.type] || { width: 200, height: 60 };
};

/**
 * Apply Dagre layout algorithm to position nodes automatically
 * 
 * This function takes unpositioned nodes and edges, runs them through
 * the Dagre layout algorithm, and returns properly positioned elements
 * that avoid overlapping.
 * 
 * @param {Array} nodes - Array of ReactFlow nodes (positions will be calculated)
 * @param {Array} edges - Array of ReactFlow edges
 * @param {Object} options - Layout configuration options
 * @param {string} options.direction - Layout direction: 'TB', 'LR', 'BT', 'RL'
 * @param {number} options.nodeSpacing - Horizontal spacing between nodes
 * @param {number} options.levelSpacing - Vertical spacing between levels
 * @returns {{ nodes: Array, edges: Array }} - Layouted nodes and edges
 */
export const getLayoutedElements = (nodes, edges, options = {}) => {
    if (!nodes.length) {
        return { nodes: [], edges: [] };
    }

    const {
        direction = 'TB',
        nodeSpacing = DEFAULT_LAYOUT_OPTIONS.nodesep,
        levelSpacing = DEFAULT_LAYOUT_OPTIONS.ranksep,
    } = options;

    const isHorizontal = direction === 'LR' || direction === 'RL';

    // Create a new Dagre graph
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Configure the graph layout
    dagreGraph.setGraph({
        ...DEFAULT_LAYOUT_OPTIONS,
        rankdir: direction,
        nodesep: nodeSpacing,
        ranksep: levelSpacing,
    });

    // Add nodes to the graph with their dimensions
    nodes.forEach((node) => {
        const { width, height } = getNodeDimensions(node);
        dagreGraph.setNode(node.id, { width, height });
    });

    // Add edges to the graph
    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    // Run the Dagre layout algorithm
    dagre.layout(dagreGraph);

    // Map the calculated positions back to ReactFlow nodes
    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const { width, height } = getNodeDimensions(node);

        // Dagre returns center positions, ReactFlow uses top-left
        // Convert center coordinates to top-left corner
        const position = {
            x: nodeWithPosition.x - width / 2,
            y: nodeWithPosition.y - height / 2,
        };

        // Update handle positions based on layout direction
        return {
            ...node,
            position,
            // Set target/source positions for proper edge connections
            targetPosition: isHorizontal ? 'left' : 'top',
            sourcePosition: isHorizontal ? 'right' : 'bottom',
        };
    });

    // Update edges with proper connection types
    const layoutedEdges = edges.map((edge) => ({
        ...edge,
        // Smooth step edges work best with hierarchical layouts
        type: edge.type || 'smoothstep',
    }));

    return { nodes: layoutedNodes, edges: layoutedEdges };
};

/**
 * Create a layout configuration preset
 * @param {'vertical' | 'horizontal'} preset - Layout preset name
 * @returns {Object} - Layout options
 */
export const getLayoutPreset = (preset) => {
    const presets = {
        vertical: {
            direction: 'TB',
            nodeSpacing: 50,
            levelSpacing: 90,
        },
        horizontal: {
            direction: 'LR',
            nodeSpacing: 60,
            levelSpacing: 120,
        },
        compact: {
            direction: 'TB',
            nodeSpacing: 30,
            levelSpacing: 60,
        },
        spacious: {
            direction: 'TB',
            nodeSpacing: 80,
            levelSpacing: 120,
        },
    };

    return presets[preset] || presets.vertical;
};

export default getLayoutedElements;